import React, { useEffect, useRef, useState } from 'react';
import { alpha, Box, IconButton } from '@mui/material';
import { GridColDef, GridFilterListIcon } from '@mui/x-data-grid';
import { useDebounce } from '@uidotdev/usehooks';
import dayjs from 'dayjs';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  FilterDatePopover,
  PaymentStatusChip,
  RelativelyPositionedSearchFilter,
  SubHeader,
  Table,
} from '@/components';
import { DEBOUNCE_TIME_MS } from '@/constants';
import {
  getSavedCurrentPage,
  getSavedRowsPerPage,
  handleDeleteItems,
  numberSortComparator,
  onSelectAll,
  onSelectRow,
  Order,
  useOrders,
} from '@/core';
import { DefaultCell, PriceCell } from '@/core/renderCells';
import { OrderTableHeader } from '@/features';
import { ORDERS_ROUTE } from '@/routes';
import palette from '@/theme/palette';

const ORDERS_PAGE_KEY = 'orders';

export const PageOrders = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | undefined>(undefined);
  const [dateFilterPopoverAnchor, setDateFilterPopoverAnchor] = useState<HTMLElement | null>(null);

  //pagination states
  const [currentPage, setCurrentPage] = useState<number>(getSavedCurrentPage(ORDERS_PAGE_KEY));
  const [rowsPerPage, setRowsPerPage] = useState<number>(getSavedRowsPerPage(ORDERS_PAGE_KEY));

  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const debouncedSearchInputValue = useDebounce(searchInputValue, DEBOUNCE_TIME_MS);

  const dateFilterButtonRef = useRef<HTMLButtonElement | null>(null);

  const {
    queries: { useOrdersQuery, useOrdersSearch },
    mutations: { deleteMutation },
  } = useOrders();

  const { data, status, refetch } = useOrdersQuery({
    limit: rowsPerPage,
    page: currentPage,
    start_date: selectedDateRange?.from ? dayjs(selectedDateRange.from).format('YYYY-MM-DD') : null,
    end_date: selectedDateRange?.to ? dayjs(selectedDateRange.to).format('YYYY-MM-DD') : null,
  });

  const { data: ordersFilteredById, status: ordersFilteredByIdStatus } =
    useOrdersSearch(debouncedSearchInputValue);

  const isFilterActive = debouncedSearchInputValue.length > 0;
  const displayRows = isFilterActive ? ordersFilteredById?.results || [] : data?.results || [];
  const displayTotal = isFilterActive
    ? ordersFilteredById?.total_results || 0
    : data?.total_results || 0;
  const paginationTotal = isFilterActive
    ? ordersFilteredById?.total_results || 0
    : data?.total_results || 0;

  const handleDateFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setDateFilterPopoverAnchor(event.currentTarget);
  };

  const handleDateFilterClose = () => {
    setDateFilterPopoverAnchor(null);
  };

  const handleCancelAction = () => {
    setSelectedRows([]);
  };

  const handleDeleteOrders = async () => {
    await handleDeleteItems({
      selectedRows,
      setSelectedRows,
      refetch,
      deleteMutation,
    });
  };

  useEffect(() => {
    if (!dateFilterPopoverAnchor) refetch();
  }, [selectedDateRange, refetch, dateFilterPopoverAnchor]);

  const columns: GridColDef<Order>[] = [
    {
      field: 'id',
      headerName: t('HEADERS.ORDER_ID'),
      sortComparator: numberSortComparator,
      flex: 2,
      renderCell: (params) => <DefaultCell value={params.value} />,
    },
    {
      field: 'order_number',
      headerName: t('HEADERS.ORDER_NUMBER'),
      sortComparator: numberSortComparator,
      flex: 1,
      renderCell: (params) => <DefaultCell value={params.value} />,
    },
    {
      field: 'is_takeout',
      headerName: t('HEADERS.TYPE'),
      flex: 1,
      renderCell: (params) => (
        <DefaultCell value={params.value ? t('ORDER.TAKEOUT') : t('ORDER.EAT_IN')} />
      ),
    },
    {
      field: 'created_at',
      headerName: t('HEADERS.DATE_TIME'),
      filterable: true,
      flex: 1,
      renderHeader: () => (
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '0.5rem' }}>{t('HEADERS.DATE')}</span>
          <IconButton
            ref={dateFilterButtonRef}
            onClick={handleDateFilterClick}
            sx={
              selectedDateRange
                ? {
                    color: palette.primary.main,
                    bgcolor: alpha(palette.primary.main, 0.12),
                  }
                : {}
            }
          >
            <GridFilterListIcon />
          </IconButton>
        </Box>
      ),
      renderCell: (params) => (
        <DefaultCell value={`${dayjs(params.value).format('DD.MM.YYYY HH:mm')}`} />
      ),
    },
    {
      field: 'total_taxed_price',
      headerName: t('HEADERS.PRICE_TAXED'),
      flex: 1,
      sortable: true,
      sortComparator: (v1, v2) => {
        return Number(v1) - Number(v2);
      },
      headerAlign: 'right',
      renderCell: (params) => <PriceCell price={params.value} isBold />,
    },
    {
      field: 'status',
      headerName: t('HEADERS.ORDER_STATUS'),
      flex: 1,
      sortable: true,
      renderCell: (params) => <PaymentStatusChip status={params.value} />,
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <SubHeader />
      <RelativelyPositionedSearchFilter
        inputValue={searchInputValue}
        setInputValue={setSearchInputValue}
        placeholder={t('ORDER.SEARCH_PLACEHOLDER')}
        sx={{ marginBottom: '-60px' }}
      />
      <Table
        key={`${searchInputValue}_${currentPage}_${rowsPerPage}_${JSON.stringify(data)}_${JSON.stringify(ordersFilteredById)}`}
        rows={displayRows}
        tableId={ORDERS_PAGE_KEY}
        columns={columns}
        isDraggable={false}
        isCheckBox
        isRowsEditable
        loading={
          status === 'pending' ||
          deleteMutation.status === 'pending' ||
          ordersFilteredByIdStatus === 'pending'
        }
        editRowButtonLabel={t('GLOBAL.SHOW')}
        onEditRow={(rowId) => {
          navigate(`${ORDERS_ROUTE}/${rowId}`);
        }}
        selectedRows={selectedRows}
        onSelectRow={(selectedRowId) => onSelectRow(selectedRowId, setSelectedRows)}
        onSelectAll={() => onSelectAll(data, selectedRows, setSelectedRows)}
        pagination
        paginationOptions={{
          currentPage,
          rowsPerPage,
          totalRows: paginationTotal,
          rowCount: paginationTotal,
          rowsPerPageOptions: [15, 25, 50, 100],
          onPageSizeChange: (newPageSize: number) => {
            setRowsPerPage(newPageSize);
          },
          onPageChange: (newPage: number) => {
            setCurrentPage(newPage);
          },
          pageSize: rowsPerPage,
          page: currentPage,
        }}
        ToolBar={(props) => (
          <OrderTableHeader
            {...props}
            rows={data?.results.map((row) => row.id) || []}
            count={displayTotal}
            selectedRows={selectedRows}
            onDelete={handleDeleteOrders}
            onCancel={handleCancelAction}
          />
        )}
      />
      <FilterDatePopover
        range={selectedDateRange}
        setRange={setSelectedDateRange}
        anchorEl={dateFilterPopoverAnchor}
        handleFilterClose={handleDateFilterClose}
      />
    </Box>
  );
};
